import { VITE_CORE_API_URL } from "@/constants/env";
import { auth } from "@/services/auth-service";

export interface CreateAssetTypeRequest {
	masterAssetTypeId: number;
	name: string;
	organizationAssetTypeCategoryId?: number;
	conditionalInputs?: Record<string, unknown>;
	isDeprecated?: boolean;
	inputsNotRequired?: boolean;
	organizationEquipmentAreaId?: number;
	extra?: Record<string, unknown>;
	subOrganization?: string;
}

export interface AssetType {
	id: number;
	organizationId: number;
	masterAssetTypeId: number;
	name: string;
	organizationAssetTypeCategoryId?: number;
	conditionalInputs?: Record<string, unknown>;
	isDeprecated: boolean;
	inputsNotRequired: boolean;
	organizationEquipmentAreaId?: number;
	extra?: Record<string, unknown>;
	subOrganization?: string;
	createdAt: string;
}

export interface MasterAssetType {
	id: number;
	assetFamilyId: number;
	name: string;
	createdAt: string;
}

export interface AssetFamily {
	id: number;
	name: string;
	createdAt: string;
}

export interface Manufacturer {
	id: number;
	name: string;
	isVerified: boolean;
	timeAdded: string;
}

export interface CreateManufacturerRequest {
	name: string;
	isVerified?: boolean;
}

export const createOrganizationAssetType = async (
	assetTypeData: CreateAssetTypeRequest,
): Promise<AssetType> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/utility/asset-types`;

		const response = await fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
			body: JSON.stringify(assetTypeData),
		});

		if (response.status !== 201) {
			const errorDetails = await response.json();
			throw new Error(
				`Failed to create asset type: ${errorDetails.message || "Unknown error"}`,
			);
		}

		const result = await response.json();
		return result.data.assetType;
	} catch (exception: any) {
		throw new Error(exception.message || "Failed to create asset type");
	}
};

export const getOrganizationAssetTypes = async (
	includeDeprecated: boolean = false,
): Promise<AssetType[]> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/utility/asset-types${includeDeprecated ? "?includeDeprecated=true" : ""}`;

		const token = await auth?.currentUser?.getIdToken();

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});

		if (response.status !== 200) {
			const errorDetails = await response.json();
			console.error("Error response:", errorDetails);
			throw new Error(
				`Failed to fetch asset types: ${errorDetails.message || "Unknown error"}`,
			);
		}

		const result = await response.json();

		// Transform snake_case to camelCase
		const transformedAssetTypes = result.data.assetTypes.map(
			(assetType: any) => ({
				id: assetType.id,
				organizationId: assetType.organization_id,
				masterAssetTypeId: assetType.master_asset_type_id,
				name: assetType.name,
				organizationAssetTypeCategoryId:
					assetType.organization_asset_type_category_id,
				conditionalInputs: assetType.conditional_inputs,
				isDeprecated: assetType.is_deprecated,
				inputsNotRequired: assetType.inputs_not_required,
				organizationEquipmentAreaId: assetType.organization_equipment_area_id,
				extra: assetType.extra,
				subOrganization: assetType.sub_organization,
				createdAt: assetType.created_at,
			}),
		);

		return transformedAssetTypes;
	} catch (exception: any) {
		console.error("Exception in getOrganizationAssetTypes:", exception);
		throw new Error(exception.message || "Failed to fetch asset types");
	}
};

export const getMasterAssetTypes = async (): Promise<MasterAssetType[]> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/utility/master-asset-types`;

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			const errorDetails = await response.json();
			throw new Error(
				`Failed to fetch master asset types: ${errorDetails.message || "Unknown error"}`,
			);
		}

		const result = await response.json();

		// Backend already returns camelCase, so just return the data
		return result.data.masterAssetTypes;
	} catch (exception: any) {
		throw new Error(exception.message || "Failed to fetch master asset types");
	}
};

export const getAssetFamilies = async (): Promise<AssetFamily[]> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/utility/asset-families`;

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			const errorDetails = await response.json();
			throw new Error(
				`Failed to fetch asset families: ${errorDetails.message || "Unknown error"}`,
			);
		}

		const result = await response.json();

		// Transform snake_case to camelCase
		const transformedAssetFamilies = result.data.assetFamilies.map(
			(assetFamily: any) => ({
				id: assetFamily.id,
				name: assetFamily.name,
				createdAt: assetFamily.created_at,
			}),
		);

		return transformedAssetFamilies;
	} catch (exception: any) {
		throw new Error(exception.message || "Failed to fetch asset families");
	}
};

export const updateOrganizationAssetType = async (
	assetTypeId: number,
	updates: {
		name?: string;
		isDeprecated?: boolean;
		inputsNotRequired?: boolean;
		organizationAssetTypeCategoryId?: number;
		organizationEquipmentAreaId?: number;
		extra?: Record<string, unknown>;
	},
): Promise<AssetType> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/utility/asset-types/${assetTypeId}`;

		const response = await fetch(url, {
			method: "PATCH",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
			body: JSON.stringify(updates),
		});

		if (response.status !== 200) {
			const errorDetails = await response.json();
			throw new Error(
				`Failed to update asset type: ${errorDetails.message || "Unknown error"}`,
			);
		}

		const result = await response.json();

		// Transform snake_case to camelCase
		const assetType = result.data.assetType;
		return {
			id: assetType.id,
			organizationId: assetType.organization_id,
			masterAssetTypeId: assetType.master_asset_type_id,
			name: assetType.name,
			organizationAssetTypeCategoryId:
				assetType.organization_asset_type_category_id,
			conditionalInputs: assetType.conditional_inputs,
			isDeprecated: assetType.is_deprecated,
			inputsNotRequired: assetType.inputs_not_required,
			organizationEquipmentAreaId: assetType.organization_equipment_area_id,
			extra: assetType.extra,
			subOrganization: assetType.sub_organization,
			createdAt: assetType.created_at,
		};
	} catch (exception: any) {
		throw new Error(exception.message || "Failed to update asset type");
	}
};

export const deleteOrganizationAssetType = async (
	assetTypeId: number,
): Promise<void> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/utility/asset-types/${assetTypeId}`;

		const response = await fetch(url, {
			method: "DELETE",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			const errorDetails = await response.json();
			throw new Error(
				`Failed to delete asset type: ${errorDetails.message || "Unknown error"}`,
			);
		}
	} catch (exception: any) {
		throw new Error(exception.message || "Failed to delete asset type");
	}
};

export const getSyncStatus = async (): Promise<boolean> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/utility/sync-status`;

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			const errorDetails = await response.json();
			throw new Error(
				`Failed to fetch sync status: ${errorDetails.message || "Unknown error"}`,
			);
		}

		const result = await response.json();
		return result.data.syncEnabled;
	} catch (exception: any) {
		throw new Error(exception.message || "Failed to fetch sync status");
	}
};

export const enableServiceChannelExport = async (): Promise<void> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/utility/enable-service-channel`;

		const response = await fetch(url, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			const errorDetails = await response.json();
			throw new Error(
				`Failed to enable service channel export: ${errorDetails.message || "Unknown error"}`,
			);
		}
	} catch (exception: any) {
		throw new Error(
			exception.message || "Failed to enable service channel export",
		);
	}
};

export const disableServiceChannelExport = async (): Promise<void> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/utility/disable-service-channel`;

		const response = await fetch(url, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			const errorDetails = await response.json();
			throw new Error(
				`Failed to disable service channel export: ${errorDetails.message || "Unknown error"}`,
			);
		}
	} catch (exception: any) {
		throw new Error(
			exception.message || "Failed to disable service channel export",
		);
	}
};

export const createManufacturer = async (
	manufacturerData: CreateManufacturerRequest,
): Promise<Manufacturer> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/utility/manufacturers`;

		const response = await fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
			body: JSON.stringify(manufacturerData),
		});

		if (response.status !== 201) {
			const errorDetails = await response.json();
			throw new Error(
				`Failed to create manufacturer: ${errorDetails.message || "Unknown error"}`,
			);
		}

		const result = await response.json();
		return result.data.manufacturer;
	} catch (exception: any) {
		throw new Error(exception.message || "Failed to create manufacturer");
	}
};

export const getManufacturers = async (): Promise<Manufacturer[]> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/utility/manufacturers`;

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			const errorDetails = await response.json();
			throw new Error(
				`Failed to fetch manufacturers: ${errorDetails.message || "Unknown error"}`,
			);
		}

		const result = await response.json();
		return result.data.manufacturers;
	} catch (exception: any) {
		throw new Error(exception.message || "Failed to fetch manufacturers");
	}
};

export const deleteManufacturer = async (
	manufacturerId: number,
): Promise<void> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/utility/manufacturers/${manufacturerId}`;

		const response = await fetch(url, {
			method: "DELETE",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			const errorDetails = await response.json();
			throw new Error(
				`Failed to delete manufacturer: ${errorDetails.message || "Unknown error"}`,
			);
		}
	} catch (exception: any) {
		throw new Error(exception.message || "Failed to delete manufacturer");
	}
};
